import { useTranslation } from "react-i18next";

import { useActivityData } from "./useActivityData";
import { StateBar, StateReadout } from "./components/StateBar";
import { StateLegend } from "./components/StateLegend";
import { useLabels } from "./useLabels";
import type { CurrentStateGroup } from "./activityTypes";
import type { CohortFilters } from "./reportTypes";
import "./activity.css";

/**
 * Population snapshot — the distribution of CURRENT factor states across all
 * active whānau right now (not tied to entry/exit), rolled up by Te Pou
 * Mataaho, domain and subdomain. Answers Sue's "a snapshot to know what we are
 * dealing with". Reads the de-identified current-state aggregate from the
 * activity endpoint (currentRisk); cohort filters apply. Headings are English
 * for now — te reo to follow.
 */
type Props = { filters: CohortFilters };

function groupProportion(g: CurrentStateGroup) {
  const tot = g.assessedTotal;
  return tot > 0
    ? {
        risk: g.risk / tot,
        change: g.change / tot,
        protective: g.protective / tot,
      }
    : { risk: 0, change: 0, protective: 0 };
}

function SnapshotRows({
  groups,
  label,
}: {
  groups: CurrentStateGroup[];
  label: (id: string) => string;
}) {
  return (
    <ul className="rpt-act-current">
      {groups.map((g) => (
        <li key={g.id} className="rpt-act-current__row">
          <div className="rpt-act-current__head">
            <span className="rpt-act-current__name">{label(g.id)}</span>
            <span className="rpt-act-current__n">n={g.assessedTotal}</span>
          </div>
          {g.assessedTotal > 0 ? (
            <>
              <StateBar
                proportion={groupProportion(g)}
                phaseLabel={label(g.id)}
              />
              <StateReadout
                proportion={groupProportion(g)}
                counts={{
                  risk: g.risk,
                  change: g.change,
                  protective: g.protective,
                  notAssessed: g.notAssessed,
                }}
              />
            </>
          ) : (
            <p className="rpt-act-current__empty">Not yet assessed</p>
          )}
        </li>
      ))}
    </ul>
  );
}

export function SnapshotView({ filters }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();
  const state = useActivityData(filters);

  if (state.kind === "loading")
    return <p className="rpt__status">{t("reports.loading")}</p>;
  if (state.kind === "forbidden")
    return (
      <p className="rpt__status rpt__status--error">{t("reports.forbidden")}</p>
    );
  if (state.kind === "error")
    return (
      <p className="rpt__status rpt__status--error">
        {t("reports.error", { message: state.message })}
      </p>
    );

  const a = state.data;
  const cr = a.currentRisk;
  const asAt = new Date(a.generatedAt).toLocaleDateString("en-NZ");

  return (
    <div className="rpt-view">
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">Population snapshot</h2>
        <p className="rpt-panel__lead">
          Current factor states across all active whānau, as at {asAt}.
        </p>
        <div className="rpt__meta">
          <span className="rpt__count">
            {t("reports.cohortCount", { n: a.totalWhanau })}
          </span>
          <StateLegend />
        </div>
      </section>

      <section className="rpt-panel">
        <h3 className="rpt-subhead" lang="mi">
          Te Pou Mataaho
        </h3>
        <SnapshotRows groups={cr.byPou} label={(id) => labels.pou(id)} />
      </section>

      <section className="rpt-panel">
        <h3 className="rpt-subhead">{t("reports.tab.domain")}</h3>
        <SnapshotRows groups={cr.byDomain} label={(id) => labels.domain(id)} />
      </section>

      <section className="rpt-panel">
        <h3 className="rpt-subhead">By subdomain</h3>
        <SnapshotRows
          groups={cr.bySubdomain}
          label={(id) => labels.subdomain(id)}
        />
      </section>
    </div>
  );
}

export default SnapshotView;
